import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { PATHS } from 'router/config';

import {
  marketingSettingsHref,
  withSettingsTab,
} from '../Settings/settingsLinks';
import {
  IMarketingAliasDraft,
  IMarketingBreakdownVariables,
  IMarketingDrillState,
  IMarketingRange,
  isMarketingChannel,
  isMarketingCreativeFormat,
  isMarketingIsoDay,
  isMarketingModel,
  isMarketingPreset,
  isMarketingRootDimension,
  isMarketingTab,
  marketingBreakdownVariables,
  marketingCanDrill,
  marketingChannelFromKey,
  marketingDrillState,
  marketingHypothesisKind,
  marketingPresetRange,
  marketingRangeIssue,
  marketingTodayWarsaw,
  MARKETING_DEFAULT_DIMENSION,
  MARKETING_DEFAULT_MODEL,
  MARKETING_DEFAULT_PRESET,
  MARKETING_DEFAULT_TAB,
  MARKETING_SETTINGS_SUBTABS,
  MarketingPeriodPreset,
  MarketingSettingsSubTab,
  MarketingTabId,
} from './marketingModel';
import {
  buildMarketingInspector,
  IMarketingInspectorTarget,
  marketingSameInspector,
  parseMarketingInspector,
} from './marketingInspectorModel';
import {
  isMarketingBingDimension,
  isMarketingSearchBrandFilter,
  isMarketingSearchDimension,
  isMarketingSearchZone,
  isMarketingTrafficGroup,
  MARKETING_DEFAULT_BING_DIMENSION,
  MARKETING_DEFAULT_SEARCH_DIMENSION,
  MARKETING_DEFAULT_TRAFFIC_GROUP,
  MarketingSearchBrandFilter,
} from './marketingSearchModel';
import {
  IMarketingChannelRuleInput,
  IMarketingFilter,
  IMarketingSignal,
  MarketingAttributionModelCode,
  MarketingChannelCode,
  MarketingDimensionCode,
  MarketingSearchDimensionCode,
  MarketingSearchZoneCode,
  MarketingTrafficGroupCode,
} from './types';

/**
 * ТЗ 0088 — состояние раздела «Маркетинг» живёт в адресе (как у аналитики
 * опросов, `useAnalyticsDeepLink`): ссылку на «Meta → кампания X за прошлый
 * месяц» можно отправить коллеге, «Назад» отменяет спуск, а применённый
 * фильтр виден крошками.
 *
 * Спуск по строкам — `push` (кнопка «Назад» его отменяет); правка фильтра,
 * смена вкладки или разреза — `replace` (иначе «Назад» прошёл бы по каждому
 * клику в выпадашке). Значения по умолчанию в адрес не пишутся: чистая ссылка
 * `/marketing` — это «пульс за 30 дней, все каналы, последнее касание».
 */

/** Имена параметров — короткие: они попадают в ссылки. */
const P = {
  tab: 'tab',
  period: 'period',
  from: 'from',
  to: 'to',
  channels: 'ch',
  families: 'fam',
  offers: 'offer',
  legalEntity: 'le',
  model: 'model',
  dimension: 'dim',
  path: 'path',
  // Этап 2 — «Креативы»: только выгорающие, формат. `ad` — СТАРЫЙ адрес
  // выбранного объявления (до панели справа): читается как `insp=ad:<id>` и
  // заменяется (`replace`); новым адресом не пишется.
  fatigueOnly: 'fatigue',
  format: 'fmt',
  ad: 'ad',
  // Этап 3 — «Поиск и сайт»: разрез таблицы GSC, бренд, зона позиций,
  // семейство курса и поиск по строкам (ссылки сигналов спроса и позиций).
  searchDimension: 'sdim',
  searchBrand: 'brand',
  searchZone: 'zone',
  searchFamily: 'sfam',
  searchText: 'sq',
  // Этап 4 — «AI-разбор»: неделя, чья сводка открыта (`2026-W39`).
  week: 'week',
  // Доводка читаемости: панель справа (`channel:META`), фильтр кампаний
  // («Креативы кампании» из панели) и подвкладки длинных вкладок.
  insp: 'insp',
  campaigns: 'camp',
  stab: 'stab',
  qtab: 'qtab',
  jtab: 'jtab',
  ctab: 'ctab',
  // ТЗ 0093: «Источники и план» — источники, план, журнал выгрузки. До него
  // эти подвкладки жили в `settab` вкладки «Настройки».
  dtab: 'dtab',
  // Подвкладки «Поиска и сайта»: разрез строк снимка Bing (запросы /
  // страницы) и группировка трафика GA4 (группа каналов / источник /
  // кампания) — раньше жили в `useState` и терялись со ссылкой.
  bingDimension: 'bdim',
  trafficGroup: 'tgroup',
} as const;

/**
 * Параметры подвкладок: `stab` — «Поиск и сайт», `qtab` — «Качество данных»,
 * `jtab` — «Путь клиента», `ctab` — «Креативы», `dtab` — «Источники и план».
 * Значение — id подвкладки; какие id бывают, знает сама вкладка (здесь —
 * только форма: короткое слово), и незнакомое она откатывает к своей первой
 * подвкладке.
 */
export const MARKETING_SUBTAB_PARAMS = [
  'stab',
  'qtab',
  'jtab',
  'ctab',
  'dtab',
] as const;

export type MarketingSubTabParam = typeof MARKETING_SUBTAB_PARAMS[number];

export type IMarketingSubTabs = Readonly<
  Record<MarketingSubTabParam, string | null>
>;

export const MARKETING_SUBTABS_DEFAULT: IMarketingSubTabs = {
  stab: null,
  qtab: null,
  jtab: null,
  ctab: null,
  dtab: null,
};

/** Id подвкладки в адресе: латиница, цифры, `-`/`_`, до 32 знаков. */
const SUBTAB_RE = /^[a-z][a-z0-9_-]{0,31}$/i;

const OWN_PARAMS: string[] = Object.values(P);

export interface IMarketingUrlState {
  tab: MarketingTabId;
  preset: MarketingPeriodPreset;
  /** Свои даты — только при `preset === 'custom'`. */
  from: string | null;
  to: string | null;
  channels: MarketingChannelCode[];
  families: string[];
  offers: string[];
  legalEntity: string | null;
  model: MarketingAttributionModelCode;
  /** Корневой разрез таблицы «Каналы». */
  dimension: MarketingDimensionCode;
  /** Ключи строк, по которым спустились от корня. */
  path: string[];
  /** «Креативы»: только выгорающие объявления. */
  fatigueOnly: boolean;
  /** «Креативы»: формат объявления (`video`…); `null` — любой. */
  format: string | null;
  /** «Поиск и сайт»: разрез таблицы Search Console. */
  searchDimension: MarketingSearchDimensionCode;
  /** «Поиск и сайт»: только брендовые / только небрендовые; `null` — все. */
  searchBrand: MarketingSearchBrandFilter | null;
  /** «Поиск и сайт»: зона средней позиции (клик по полосе зон). */
  searchZone: MarketingSearchZoneCode | null;
  /** «Поиск и сайт»: семейство курса строк (`none` — без семейства). */
  searchFamily: string | null;
  /** «Поиск и сайт»: подстрока запроса или страницы. */
  searchText: string;
  /** «AI-разбор»: ISO-неделя сводки; `null` — последняя. */
  week: string | null;
  /**
   * Кампании общего фильтра (id `AdEntity`): «Креативы кампании» из панели.
   * В фильтр-баре своей выпадашки нет — снимается крошкой.
   */
  campaigns: string[];
  /** Панель справа: что открыто; `null` — закрыта. */
  insp: IMarketingInspectorTarget | null;
  /** Подвкладки длинных вкладок; `null` — первая (по умолчанию). */
  subTabs: IMarketingSubTabs;
  /** «Поиск и сайт» → Bing: разрез строк снимка (`query` | `page`). */
  bingDimension: MarketingSearchDimensionCode;
  /** «Поиск и сайт» → «Сайт»: группировка трафика GA4. */
  trafficGroup: MarketingTrafficGroupCode;
}

export const MARKETING_URL_DEFAULTS: IMarketingUrlState = {
  tab: MARKETING_DEFAULT_TAB,
  preset: MARKETING_DEFAULT_PRESET,
  from: null,
  to: null,
  channels: [],
  families: [],
  offers: [],
  legalEntity: null,
  model: MARKETING_DEFAULT_MODEL,
  dimension: MARKETING_DEFAULT_DIMENSION,
  path: [],
  fatigueOnly: false,
  format: null,
  searchDimension: MARKETING_DEFAULT_SEARCH_DIMENSION,
  searchBrand: null,
  searchZone: null,
  searchFamily: null,
  searchText: '',
  week: null,
  campaigns: [],
  insp: null,
  subTabs: MARKETING_SUBTABS_DEFAULT,
  bingDimension: MARKETING_DEFAULT_BING_DIMENSION,
  trafficGroup: MARKETING_DEFAULT_TRAFFIC_GROUP,
};

/** Список через запятую — ссылка с мультивыбором остаётся читаемой. */
const readList = (params: URLSearchParams, name: string): string[] =>
  (params.get(name) ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const unique = <T>(items: T[]): T[] => Array.from(new Set(items));

/** ISO-неделя адреса: `2026-W39`; мусор — `null`. */
export const MARKETING_ISO_WEEK_RE = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;

/**
 * Канал адреса — кодом enum (`META`). Ссылка сервера (связь задачи
 * «сигнал маркетинга») пишет код базы (`meta`): без перевода фильтр молча
 * терял бы канал, а спуск — сужение по нему.
 */
const channelOf = (value: string): string => marketingChannelFromKey(value);

/**
 * Старая ссылка «Креативов» с выбранным объявлением (`?ad=295`, до панели
 * справа): id объявления, если он годится; иначе `null`.
 */
export const marketingLegacyAd = (search: string): string | null => {
  const raw = (new URLSearchParams(search).get(P.ad) ?? '').trim();
  return /^\d+$/.test(raw) ? raw : null;
};

/**
 * Разбор адреса. Всё незнакомое откатывается к значению по умолчанию, а не
 * роняет экран: ссылки живут дольше словарей.
 */
export const parseMarketingSearch = (search: string): IMarketingUrlState => {
  const params = new URLSearchParams(search);

  const rawTab = params.get(P.tab);
  const tab = isMarketingTab(rawTab) ? rawTab : MARKETING_URL_DEFAULTS.tab;

  const rawPreset = params.get(P.period);
  let preset: MarketingPeriodPreset = isMarketingPreset(rawPreset)
    ? rawPreset
    : MARKETING_URL_DEFAULTS.preset;
  let from: string | null = null;
  let to: string | null = null;
  if (preset === 'custom') {
    const range = {
      from: params.get(P.from) ?? '',
      to: params.get(P.to) ?? '',
    };
    if (marketingRangeIssue(range) === null) {
      from = range.from;
      to = range.to;
    } else {
      // Битые или перевёрнутые даты — не «пустой экран», а период по умолчанию.
      preset = MARKETING_URL_DEFAULTS.preset;
    }
  }

  const rawModel = params.get(P.model);
  const rawDimension = params.get(P.dimension);
  const dimension = isMarketingRootDimension(rawDimension)
    ? rawDimension
    : MARKETING_URL_DEFAULTS.dimension;
  const rawPath = readList(params, P.path);
  const path = marketingDrillState(
    dimension,
    dimension === 'CHANNEL' && rawPath.length
      ? [channelOf(rawPath[0]), ...rawPath.slice(1)]
      : rawPath,
  ).steps.map((step) => step.key);
  const rawFormat = params.get(P.format);
  // Старая ссылка `?ad=` открывает панель объявления, если своей нет.
  const legacyAd = marketingLegacyAd(search);
  const rawSearchDimension = params.get(P.searchDimension);
  const rawBrand = params.get(P.searchBrand);
  const rawZone = params.get(P.searchZone);
  const rawWeek = (params.get(P.week) ?? '').trim();
  const rawBing = params.get(P.bingDimension);
  const rawTrafficGroup = params.get(P.trafficGroup);
  const subTabs = MARKETING_SUBTAB_PARAMS.reduce<
    Record<MarketingSubTabParam, string | null>
  >(
    (acc, name) => {
      const value = (params.get(P[name]) ?? '').trim();
      return { ...acc, [name]: SUBTAB_RE.test(value) ? value : null };
    },
    { ...MARKETING_SUBTABS_DEFAULT },
  );

  return {
    tab,
    preset,
    from,
    to,
    channels: unique(
      readList(params, P.channels).map(channelOf).filter(isMarketingChannel),
    ),
    families: unique(readList(params, P.families)),
    offers: unique(readList(params, P.offers)),
    legalEntity: params.get(P.legalEntity) || null,
    model: isMarketingModel(rawModel) ? rawModel : MARKETING_URL_DEFAULTS.model,
    dimension,
    path,
    fatigueOnly: params.get(P.fatigueOnly) === '1',
    format: isMarketingCreativeFormat(rawFormat) ? rawFormat : null,
    searchDimension: isMarketingSearchDimension(rawSearchDimension)
      ? rawSearchDimension
      : MARKETING_URL_DEFAULTS.searchDimension,
    searchBrand: isMarketingSearchBrandFilter(rawBrand) ? rawBrand : null,
    searchZone: isMarketingSearchZone(rawZone) ? rawZone : null,
    searchFamily: (params.get(P.searchFamily) ?? '').trim() || null,
    searchText: (params.get(P.searchText) ?? '').trim(),
    week: MARKETING_ISO_WEEK_RE.test(rawWeek) ? rawWeek : null,
    // id кампании — число: мусор в адресе не сужает ничего.
    campaigns: unique(
      readList(params, P.campaigns).filter((id) => /^\d+$/.test(id)),
    ),
    insp:
      parseMarketingInspector(params.get(P.insp)) ??
      (legacyAd ? { kind: 'ad', key: legacyAd } : null),
    subTabs,
    // Мусор (`bdim=country` — у Bing такого снимка нет) — по умолчанию.
    bingDimension: isMarketingBingDimension(rawBing)
      ? rawBing
      : MARKETING_URL_DEFAULTS.bingDimension,
    trafficGroup: isMarketingTrafficGroup(rawTrafficGroup)
      ? rawTrafficGroup
      : MARKETING_URL_DEFAULTS.trafficGroup,
  };
};

/**
 * Строка запроса для состояния. Чужие параметры адреса (`base`) сохраняются —
 * соседняя фича (ящик продажи, например) не должна терять свои при смене
 * периода. Значения по умолчанию не пишутся.
 */
export const buildMarketingSearch = (
  state: IMarketingUrlState,
  base = '',
): string => {
  const next = new URLSearchParams(base);
  OWN_PARAMS.forEach((name) => next.delete(name));
  const d = MARKETING_URL_DEFAULTS;
  if (state.tab !== d.tab) next.set(P.tab, state.tab);
  if (state.preset !== d.preset) next.set(P.period, state.preset);
  if (state.preset === 'custom' && state.from && state.to) {
    next.set(P.from, state.from);
    next.set(P.to, state.to);
  }
  if (state.channels.length) next.set(P.channels, state.channels.join(','));
  if (state.families.length) next.set(P.families, state.families.join(','));
  if (state.offers.length) next.set(P.offers, state.offers.join(','));
  if (state.legalEntity) next.set(P.legalEntity, state.legalEntity);
  if (state.model !== d.model) next.set(P.model, state.model);
  if (state.dimension !== d.dimension) next.set(P.dimension, state.dimension);
  if (state.path.length) next.set(P.path, state.path.join(','));
  if (state.fatigueOnly) next.set(P.fatigueOnly, '1');
  if (state.format) next.set(P.format, state.format);
  if (state.searchDimension !== d.searchDimension) {
    next.set(P.searchDimension, state.searchDimension);
  }
  if (state.searchBrand) next.set(P.searchBrand, state.searchBrand);
  if (state.searchZone) next.set(P.searchZone, state.searchZone);
  if (state.searchFamily) next.set(P.searchFamily, state.searchFamily);
  if (state.searchText.trim()) next.set(P.searchText, state.searchText.trim());
  if (state.week) next.set(P.week, state.week);
  if (state.campaigns.length) next.set(P.campaigns, state.campaigns.join(','));
  if (state.insp) next.set(P.insp, buildMarketingInspector(state.insp));
  MARKETING_SUBTAB_PARAMS.forEach((name) => {
    const value = state.subTabs[name];
    if (value) next.set(P[name], value);
  });
  if (state.bingDimension !== d.bingDimension) {
    next.set(P.bingDimension, state.bingDimension);
  }
  if (state.trafficGroup !== d.trafficGroup) {
    next.set(P.trafficGroup, state.trafficGroup);
  }
  const query = next.toString();
  return query ? `?${query}` : '';
};

/**
 * Окно дат состояния. Свой период без дат (так не бывает после разбора, но
 * тип это допускает) — окно по умолчанию.
 */
export const marketingUrlRange = (
  state: IMarketingUrlState,
  today: string,
): IMarketingRange =>
  state.preset === 'custom' &&
  state.from &&
  state.to &&
  isMarketingIsoDay(state.from) &&
  isMarketingIsoDay(state.to)
    ? { from: state.from, to: state.to }
    : marketingPresetRange(
        state.preset === 'custom' ? MARKETING_DEFAULT_PRESET : state.preset,
        today,
      );

/** Общий фильтр страницы (без спуска) — переменная `filter` всех запросов. */
export const marketingUrlFilter = (
  state: IMarketingUrlState,
  today: string,
): IMarketingFilter => {
  const range = marketingUrlRange(state, today);
  return {
    dateFrom: range.from,
    dateTo: range.to,
    channels: state.channels.length ? state.channels : null,
    familySlugs: state.families.length ? state.families : null,
    offers: state.offers.length ? state.offers : null,
    // Ключ — только когда сужает: без него фильтр прежней формы, и запросы
    // других вкладок не делятся на «с пустым списком» и «без него».
    ...(state.campaigns.length ? { campaignIds: state.campaigns } : {}),
    legalEntityId: state.legalEntity,
    model: state.model,
  };
};

/**
 * Сужены ли строки «Поиска и сайта» (бренд, зона, курс, поиск). Разрез
 * таблицы — не сужение: он выбирает, ЧТО показывать.
 */
export const marketingHasSearchFilters = (
  state: Pick<
    IMarketingUrlState,
    'searchBrand' | 'searchZone' | 'searchFamily' | 'searchText'
  >,
): boolean =>
  Boolean(state.searchBrand) ||
  Boolean(state.searchZone) ||
  Boolean(state.searchFamily) ||
  Boolean(state.searchText.trim());

/** Есть ли что сбрасывать кнопкой «Сбросить фильтры». */
export const marketingHasFilters = (state: IMarketingUrlState): boolean =>
  state.channels.length > 0 ||
  state.families.length > 0 ||
  state.offers.length > 0 ||
  Boolean(state.legalEntity) ||
  state.campaigns.length > 0 ||
  state.model !== MARKETING_DEFAULT_MODEL ||
  state.path.length > 0 ||
  state.fatigueOnly ||
  Boolean(state.format) ||
  marketingHasSearchFilters(state);

/**
 * Куда ведёт сигнал «Пульса» — состояние адреса, в котором виден его
 * предмет, за ОКНО сигнала (цифры экрана те же, что в доказательствах):
 *  - выгорание и «расход без лидов» объявления — «Креативы» с панелью этого
 *    объявления справа (`insp=ad:<id>`; выгорание — ещё и «только
 *    выгорающие»);
 *  - «расход без лидов» и скачок CPL кампании — «Каналы», спуск в неё;
 *    скачок CPL канала — «Каналы», спуск в канал;
 *  - потеря меток, несопоставленные кампании, покрытие продаж — «Качество
 *    данных» (там же и лечение);
 *  - устаревший источник — «Источники и план» → источники (ТЗ 0093; до него
 *    «Настройки», и задача сервера `?tab=settings` уводится туда же);
 *  - задержанный отзыв офлайн-конверсий (ТЗ 0091) — «Источники и план →
 *    Офлайн-конверсии» (`dtab=offline`; задачу сервера `?tab=settings&
 *    settab=offline` страница уводит туда же);
 *  - спрос (этап 3) — «Поиск и сайт», небрендовые запросы этого курса
 *    (ровно то, что сигнал считал);
 *  - падение позиции страницы — «Поиск и сайт», пары «запрос × страница»,
 *    поиск по главному запросу страницы;
 *  - гипотеза бюджета (этап 4) — как ссылка сервера в задаче: «масштабировать»
 *    и «урезать» — кампания в «Каналах», «обновить креатив» — выгорающие
 *    объявления площадки (открыт самый затратный), «починить разметку» —
 *    «Качество данных» по площадке.
 * Общие фильтры снимаются: сужение, спрятавшее предмет сигнала, выглядело
 * бы как «ссылка никуда». `null` — вести некуда (незнакомый вид).
 */
export const marketingSignalLinkState = (
  signal: IMarketingSignal,
  current: IMarketingUrlState,
): IMarketingUrlState | null => {
  const base: IMarketingUrlState = {
    ...MARKETING_URL_DEFAULTS,
    model: current.model,
    preset: 'custom',
    from: signal.windowFrom,
    to: signal.windowTo,
  };
  if (
    marketingRangeIssue({ from: signal.windowFrom, to: signal.windowTo }) !==
    null
  ) {
    return null;
  }
  const level =
    typeof signal.params.level === 'string'
      ? signal.params.level
      : signal.metrics.level;
  const channel =
    signal.channel && isMarketingChannel(signal.channel)
      ? signal.channel
      : null;
  const campaignPath = (entityId: string | null | undefined) =>
    channel
      ? {
          ...base,
          tab: 'channels' as const,
          path: entityId ? [channel, entityId] : [channel],
        }
      : null;
  const adPanel = (id: string | null | undefined) =>
    id && /^\d+$/.test(id) ? { kind: 'ad' as const, key: id } : null;
  switch (signal.kind) {
    case 'creative_fatigue':
      return {
        ...base,
        tab: 'creatives',
        fatigueOnly: true,
        insp: adPanel(signal.entityId),
      };
    case 'spend_no_leads':
      if (level === 'ad' && signal.entityId) {
        return { ...base, tab: 'creatives', insp: adPanel(signal.entityId) };
      }
      return campaignPath(signal.entityId);
    case 'cpl_spike':
      return campaignPath(signal.entityId);
    case 'tracking_gap':
    case 'campaign_mapping_gap':
    case 'coverage_drop':
      return { ...base, tab: 'quality' };
    case 'source_stale':
      return {
        ...current,
        tab: 'data',
        insp: null,
        subTabs: MARKETING_SUBTABS_DEFAULT,
      };
    case 'offline_retract_hold':
      return {
        ...current,
        tab: 'data',
        insp: null,
        subTabs: { ...MARKETING_SUBTABS_DEFAULT, dtab: 'offline' },
      };
    case 'demand_unserved':
    case 'demand_rising': {
      const family =
        signal.familySlug ||
        (typeof signal.params.family === 'string' ? signal.params.family : '');
      return {
        ...base,
        tab: 'search',
        searchDimension: 'query',
        searchBrand: 'nonbrand',
        searchFamily: family || null,
      };
    }
    case 'seo_position_drop': {
      const query =
        typeof signal.params.query === 'string'
          ? signal.params.query
          : signal.metrics.query;
      return {
        ...base,
        tab: 'search',
        searchDimension: 'query_page',
        searchText: typeof query === 'string' ? query : '',
      };
    }
    case 'budget_hypothesis': {
      const kind = marketingHypothesisKind(signal);
      const channels = channel ? [channel] : [];
      if (kind === 'fix_tracking') return { ...base, tab: 'quality', channels };
      if (kind === 'refresh_creative') {
        const top = Array.isArray(signal.metrics.top_fatigued_ads)
          ? (signal.metrics.top_fatigued_ads[0] as Record<string, unknown>)
          : null;
        const adId = top && top.ad_id !== undefined ? String(top.ad_id) : '';
        return {
          ...base,
          tab: 'creatives',
          fatigueOnly: true,
          channels,
          insp: adPanel(adId),
        };
      }
      return kind ? campaignPath(signal.entityId) : null;
    }
    default:
      return null;
  }
};

/* ── ТЗ 0093: «Настройки» уехали в хаб ──────────────────────────────── */

/** Старая вкладка и её подвкладки: `?tab=settings&settab=<id>`. */
const LEGACY_SETTINGS_TAB = 'settings';
const LEGACY_SETTINGS_SUBTAB = 'settab';

/** Подвкладки старых «Настроек», которые стали разделом хаба. */
const LEGACY_CONFIG_SUBTABS: ReadonlyArray<string> = MARKETING_SETTINGS_SUBTABS;

/**
 * Куда ведёт старый адрес вкладки «Настройки». Он вшит в задачи в БД
 * (сервер писал `?tab=settings` для «источник устарел» и
 * `?tab=settings&settab=offline` для задержанного отзыва), в закладки и
 * ссылки коллегам — поэтому это редирект навсегда:
 *  - справочники (`channels`, `courses`, `signals`) — «Настройки →
 *    Маркетинг», на ту же вкладку;
 *  - операционка (`sources`, `plan`, `offline`) — «Источники и план» этой
 *    страницы, на ту же подвкладку, с остальными параметрами адреса (панель
 *    продажи из журнала выгрузки, период);
 *  - без подвкладки или с незнакомой — тоже «Источники и план»: ровно такой
 *    адрес сервер писал в задачу «источник устарел», а человеку, который
 *    искал справочники, вкладка показывает ссылку на «Настройки → Маркетинг».
 * Не старый адрес — `null`.
 */
export const marketingLegacySettingsTarget = (
  search: string,
): string | null => {
  const params = new URLSearchParams(search);
  if (params.get(P.tab) !== LEGACY_SETTINGS_TAB) return null;
  const settab = (params.get(LEGACY_SETTINGS_SUBTAB) ?? '').trim();
  if (LEGACY_CONFIG_SUBTABS.includes(settab)) {
    return marketingSettingsHref(settab as MarketingSettingsSubTab);
  }
  params.delete(LEGACY_SETTINGS_SUBTAB);
  params.set(P.tab, 'data');
  if (settab === 'plan' || settab === 'offline') params.set(P.dtab, settab);
  else params.delete(P.dtab);
  return `${PATHS.marketing}?${params.toString()}`;
};

/**
 * ТЗ 0093: черновик правила из «Качества данных» едет в «Настройки →
 * Маркетинг» в `location.state`, а не в адресе: форма правила откроется
 * сразу, а ссылка без черновика остаётся просто ссылкой на раздел. Раздел
 * снимает черновик, как только форма его приняла, — перезагрузка и «Назад»
 * форму второй раз не откроют.
 */
export interface IMarketingSettingsDrafts {
  /** «Создать правило» у источника — вкладка каналов. */
  ruleDraft?: IMarketingChannelRuleInput | null;
  /** «Сопоставить» у кампании — вкладка курсов. */
  aliasDraft?: IMarketingAliasDraft | null;
}

const DRAFTS_STATE_KEY = 'marketingDrafts';

/** Черновики из `location.state`; нет — пустой объект. */
export const marketingSettingsDraftsOf = (
  state: unknown,
): IMarketingSettingsDrafts => {
  const value =
    state && typeof state === 'object'
      ? (state as Record<string, unknown>)[DRAFTS_STATE_KEY]
      : null;
  return value && typeof value === 'object'
    ? (value as IMarketingSettingsDrafts)
    : {};
};

/** `location.state` с черновиками (для `history.replace`, когда их снимают). */
export const marketingSettingsDraftsState = (
  drafts: IMarketingSettingsDrafts,
): Record<string, IMarketingSettingsDrafts> => ({ [DRAFTS_STATE_KEY]: drafts });

/** Адрес вкладки «Настройки → Маркетинг» с черновиком в `state`. */
export const marketingSettingsLocation = (
  tab: MarketingSettingsSubTab,
  drafts?: IMarketingSettingsDrafts,
) => {
  const query = withSettingsTab('', tab, MARKETING_SETTINGS_SUBTABS);
  return {
    pathname: PATHS.settingsMarketing,
    search: query ? `?${query}` : '',
    state: drafts ? marketingSettingsDraftsState(drafts) : undefined,
  };
};

type Patch = Partial<Omit<IMarketingUrlState, 'tab' | 'path' | 'dimension'>>;

/** Запись адреса: `push` — шаг, который «Назад» отменяет. */
export interface IMarketingWriteOptions {
  push?: boolean;
}

export interface IMarketingDeepLink {
  state: IMarketingUrlState;
  range: IMarketingRange;
  /** Общий фильтр — для «Пульса», «Курсов», «Качества». */
  filter: IMarketingFilter;
  /** Спуск таблицы «Каналы»: пройденные шаги и текущий разрез. */
  drill: IMarketingDrillState;
  /** Переменные `marketingBreakdown` с учётом спуска. */
  breakdown: IMarketingBreakdownVariables;
  setTab: (tab: MarketingTabId) => void;
  /** Быстрый период; `custom` оставляет текущее окно своими датами. */
  setPreset: (preset: MarketingPeriodPreset) => void;
  setCustomRange: (range: IMarketingRange) => void;
  setFilters: (patch: Patch) => void;
  /** Сменить корень разреза — путь спуска сбрасывается. */
  setDimension: (dimension: MarketingDimensionCode) => void;
  /** Клик по строке: спуститься в неё (`push`). */
  drillInto: (key: string) => void;
  /** Крошка спуска: оставить первые `depth` шагов. */
  drillTo: (depth: number) => void;
  /** Снять фильтры и спуск; вкладка и период остаются. */
  clearFilters: () => void;
  /**
   * Несколько полей одной записью (`replace`; `push` — если переход должен
   * отменяться «Назад»): два вызова подряд пишут адрес от одного и того же
   * состояния, и второй затёр бы первый. Смена вкладки закрывает панель
   * справа, если патч не открыл её явно.
   */
  update: (
    patch: Partial<IMarketingUrlState>,
    options?: IMarketingWriteOptions,
  ) => void;
  /**
   * Открыть панель справа: из закрытой — `push` («Назад» закрывает её),
   * переключить на другую сущность при открытой — `replace`. Та же сущность
   * — ничего.
   */
  openInspector: (target: IMarketingInspectorTarget) => void;
  /** Закрыть панель (`replace`). */
  closeInspector: () => void;
  /** Подвкладка длинной вкладки (`replace`); `null` — первая. */
  setSubTab: (param: MarketingSubTabParam, value: string | null) => void;
  /**
   * Адрес другого состояния — для ссылки (`<Link to>`): сигнал «Пульса»
   * ведёт на вкладку своего предмета, и «Назад» возвращает в «Пульс».
   */
  hrefFor: (next: IMarketingUrlState) => { pathname: string; search: string };
}

export const useMarketingDeepLink = (): IMarketingDeepLink => {
  const history = useHistory();
  const { search, pathname } = useLocation();

  const state = useMemo(() => parseMarketingSearch(search), [search]);
  // Строка, а не Date: одинаковый день даёт тот же фильтр по ссылке, и
  // зависящие от него запросы не перезапрашиваются на каждом рендере.
  const today = marketingTodayWarsaw();

  const range = useMemo(() => marketingUrlRange(state, today), [state, today]);
  const filter = useMemo(() => marketingUrlFilter(state, today), [
    state,
    today,
  ]);
  const drill = useMemo(
    () => marketingDrillState(state.dimension, state.path),
    [state],
  );
  const breakdown = useMemo(
    () => marketingBreakdownVariables(filter, state.dimension, state.path),
    [filter, state],
  );

  // Старая ссылка `?ad=295` уже открыла панель объявления (разбор) —
  // заменить адрес новым (`insp=ad:295`), не добавляя шаг в историю.
  const legacy = marketingLegacyAd(search) !== null;
  useEffect(() => {
    if (!legacy) return;
    history.replace({ pathname, search: buildMarketingSearch(state, search) });
  }, [legacy, history, pathname, search, state]);

  const write = useCallback(
    (
      next: IMarketingUrlState,
      { push = false }: IMarketingWriteOptions = {},
    ) => {
      // Панель справа принадлежит вкладке: переход на другую её закрывает
      // (если сам переход её не открыл), иначе на новой вкладке висела бы
      // панель строки, которой там нет.
      const written =
        next.tab !== state.tab && next.insp === state.insp
          ? { ...next, insp: null }
          : next;
      const target = {
        pathname,
        search: buildMarketingSearch(written, search),
      };
      if (push) history.push(target);
      else history.replace(target);
    },
    [history, pathname, search, state.tab, state.insp],
  );

  // Смена вкладки снимает панель, подвкладки и их вид (разрез Bing,
  // группировку трафика): всё это относилось к прежней вкладке.
  const setTab = useCallback(
    (tab: MarketingTabId) => {
      if (tab === state.tab) return;
      write({
        ...state,
        tab,
        insp: null,
        subTabs: MARKETING_SUBTABS_DEFAULT,
        bingDimension: MARKETING_URL_DEFAULTS.bingDimension,
        trafficGroup: MARKETING_URL_DEFAULTS.trafficGroup,
      });
    },
    [write, state],
  );

  const setPreset = useCallback(
    (preset: MarketingPeriodPreset) =>
      write(
        preset === 'custom'
          ? { ...state, preset, from: range.from, to: range.to }
          : { ...state, preset, from: null, to: null },
      ),
    [write, state, range],
  );

  const setCustomRange = useCallback(
    (next: IMarketingRange) =>
      write({ ...state, preset: 'custom', from: next.from, to: next.to }),
    [write, state],
  );

  const setFilters = useCallback(
    (patch: Patch) => write({ ...state, ...patch }),
    [write, state],
  );

  const setDimension = useCallback(
    (dimension: MarketingDimensionCode) =>
      write({ ...state, dimension, path: [] }),
    [write, state],
  );

  const drillInto = useCallback(
    (key: string) => {
      if (!key || !marketingCanDrill(drill.dimension)) return;
      write({ ...state, path: [...state.path, key] }, { push: true });
    },
    [write, state, drill.dimension],
  );

  const drillTo = useCallback(
    (depth: number) =>
      write({ ...state, path: state.path.slice(0, Math.max(0, depth)) }),
    [write, state],
  );

  const clearFilters = useCallback(
    () =>
      write({
        ...MARKETING_URL_DEFAULTS,
        tab: state.tab,
        preset: state.preset,
        from: state.from,
        to: state.to,
        dimension: state.dimension,
        // Неделя «AI-разбора», панель, подвкладки и их вид — не фильтр:
        // сброс их не трогает.
        week: state.week,
        insp: state.insp,
        subTabs: state.subTabs,
        bingDimension: state.bingDimension,
        trafficGroup: state.trafficGroup,
      }),
    [write, state],
  );

  const update = useCallback(
    (patch: Partial<IMarketingUrlState>, options?: IMarketingWriteOptions) =>
      write({ ...state, ...patch }, options),
    [write, state],
  );

  const openInspector = useCallback(
    (target: IMarketingInspectorTarget) => {
      if (marketingSameInspector(state.insp, target)) return;
      write({ ...state, insp: target }, { push: !state.insp });
    },
    [write, state],
  );

  const closeInspector = useCallback(() => {
    if (!state.insp) return;
    write({ ...state, insp: null });
  }, [write, state]);

  const setSubTab = useCallback(
    (param: MarketingSubTabParam, value: string | null) =>
      write({
        ...state,
        subTabs: {
          ...state.subTabs,
          [param]: value && SUBTAB_RE.test(value) ? value : null,
        },
      }),
    [write, state],
  );

  const hrefFor = useCallback(
    (next: IMarketingUrlState) => ({
      pathname,
      search: buildMarketingSearch(next, search),
    }),
    [pathname, search],
  );

  return {
    state,
    range,
    filter,
    drill,
    breakdown,
    setTab,
    setPreset,
    setCustomRange,
    setFilters,
    setDimension,
    drillInto,
    drillTo,
    clearFilters,
    update,
    openInspector,
    closeInspector,
    setSubTab,
    hrefFor,
  };
};
